/**
 * A figure inside a knowledge article (#1637).
 *
 * ── WHAT A READER GETS, AND WHY EACH PART IS NOT OPTIONAL ──────────────────
 *
 *   <figure>          the semantic container, so the caption is bound to the
 *                     image rather than being a paragraph that happens to sit
 *                     under one
 *   <img alt>         the alt text the PUBLISHER wrote. Never the caption and
 *                     never the figure id: an alt of "TL-48" tells a
 *                     screen-reader user nothing, and an alt equal to the
 *                     caption makes the caption be read twice
 *   <figcaption>      the visible caption
 *   <ol> of callouts  the ACCESSIBLE EQUIVALENT of the numbers drawn on the
 *                     image. It is always rendered, never hover-only, because
 *                     a numbered annotation a person cannot see is the whole
 *                     content of an annotated figure
 *   an Enlarge button the frame itself is the control, it has a real
 *                     accessible name, and the overlay is @solum/ui's Modal —
 *                     which traps focus, closes on Escape and RESTORES focus
 *                     to the frame that opened it
 *
 * ── THE RECOVERY STATES ARE PART OF THE SLICE, NOT THE UNHAPPY PATH ────────
 * A figure has five states and four of them are not "an image". Each is
 * announced, each keeps the ARTICLE readable, and none of them is a spinner
 * that never resolves:
 *
 *   loading   an aspect-ratio placeholder (so the prose does not jump when the
 *             bytes land) with `aria-busy`
 *   ready     the image
 *   missing   the body names a figure the caller's manifest does not hold —
 *             withdrawn, superseded, or not visible to them. The MARKER TEXT
 *             IS NOT SHOWN: the article must never leak `{{fig:…}}`
 *   failed    the ticket or the object fetch failed. Says so, offers Try
 *             again, and STILL shows the caption and the callout list, which
 *             are the part of a figure that survives without its bytes
 *   offline   the network is unreachable; same shape, different sentence
 *
 * ── NO URL IS EVER CONSTRUCTED HERE ────────────────────────────────────────
 * The component asks `useKbAssetUrl` for a bounded URL each time it needs one
 * and never stores an object key. It cannot: `kb_article_assets` does not
 * return one.
 *
 * Since #1637 phase B that URL is a `blob:` handle on bytes already streamed
 * under the caller's own session, not a signed link, so this component now owns
 * a resource: it REVOKES the handle when it is replaced and when the figure
 * unmounts. Without that, a reader who opens six articles leaves six decoded
 * images pinned in memory for the life of the tab — and, worse, leaves a live
 * handle on private bytes lying around in a document that has moved on.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { Button } from '../controls/Button'
import { Modal } from '../controls/Modal'
import { formatBytes, formatLabel } from './articleTokens'
import type { KbAsset, KbCallout } from './types'
import s from './kb.module.css'

type Load =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'failed'; offline: boolean }

export interface KbFigureProps {
  articleId: string
  asset: KbAsset
  /** Ask the server for a bounded URL. Injected so the component is testable
   *  without a network, and so nothing here can invent one. */
  requestUrl: (input: { articleId: string; assetKey: string }) => Promise<string>
  /** 1-based position in the article, for "Figure 3". */
  index?: number
}

export function KbFigure({ articleId, asset, requestUrl, index }: KbFigureProps) {
  const [load, setLoad] = useState<Load>({ kind: 'idle' })
  const [zoomed, setZoomed] = useState(false)
  const frameRef = useRef<HTMLButtonElement | null>(null)
  const alive = useRef(true)
  // The act comes from a mutation hook whose wrapper identity is not part of a
  // figure's data identity. Keep the latest caller without turning every host
  // render into a second byte request; article + asset key are the only facts
  // that invalidate the load.
  const requestUrlRef = useRef(requestUrl)
  useEffect(() => { requestUrlRef.current = requestUrl }, [requestUrl])
  useEffect(() => {
    // React StrictMode deliberately mounts, cleans up, and mounts effects again.
    // Re-arm the guard on every setup so the second request may publish its URL.
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const fetchUrl = useCallback(async () => {
    setLoad({ kind: 'loading' })
    try {
      const url = await requestUrlRef.current({ articleId, assetKey: asset.asset_key })
      if (alive.current) setLoad({ kind: 'ready', url })
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      if (alive.current) {
        setLoad({ kind: 'failed', offline: /fetch|network|offline/i.test(msg) })
      }
    }
  }, [articleId, asset.asset_key])

  useEffect(() => { void fetchUrl() }, [fetchUrl])

  // Release the object URL when it is superseded or the figure goes away.
  // `URL.revokeObjectURL` is a no-op on anything that is not a blob handle, so
  // this stays correct if the hook ever hands back a plain URL again.
  useEffect(() => {
    if (load.kind !== 'ready') return
    const url = load.url
    return () => { URL.revokeObjectURL(url) }
  }, [load])

  const callouts = normaliseCallouts(asset.callouts)
  const number = typeof index === 'number' ? index : null
  const calloutsId = `kb-callouts-${asset.asset_key}`
  // The alt is the PUBLISHER's description. Where they left it blank the image
  // is decorative to a screen reader and the caption carries the meaning —
  // `alt=""` is the correct answer there, not a manufactured one.
  const alt = asset.alt_text ?? ''

  return (
    <figure className={s.figure} data-testid={`kb-figure-${asset.asset_key}`}>
      {load.kind === 'ready' ? (
        <Button
          ref={frameRef}
          type="button"
          variant="ghost"
          className={s.frame}
          style={aspect(asset)}
          onClick={() => setZoomed(true)}
          aria-label={`Enlarge ${asset.caption ?? alt ?? 'this figure'}`}
          data-testid={`kb-figure-zoom-${asset.asset_key}`}
        >
          {/* `onError` below is the reason this element carries a handler at all,
              and it is not an INTERACTION: nobody clicks or types on it, and no
              keyboard user is denied anything by it. It is the browser saying
              the bytes did not arrive — an expired signed window, a corrupted
              object — and without it a failure after the URL was minted leaves a
              broken-image glyph where a recovery state belongs. The interactive
              element here is the enclosing <button>, which has a real
              accessible name. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <img
            className={s.image}
            src={load.url}
            alt={alt}
            width={asset.width ?? undefined}
            height={asset.height ?? undefined}
            /* The bytes are private and bounded; a referrer would leak the
               article a reader is on to nothing useful. */
            referrerPolicy="no-referrer"
            decoding="async"
            /* A failure AFTER the URL was minted (an expired window, a
               corrupted object) lands in the same recovery state as a failure
               to mint one — a broken-image glyph is not a recovery state. */
            onError={() => setLoad({ kind: 'failed', offline: false })}
            aria-describedby={callouts.length ? calloutsId : undefined}
          />
          <span className={s.zoomHint} aria-hidden="true">Enlarge</span>
        </Button>
      ) : (
        <FigureState state={load} onRetry={() => void fetchUrl()} asset={asset} />
      )}

      {(asset.caption || number !== null) && (
        <figcaption className={s.caption}>
          {number !== null && <span className={s.captionNumber}>Figure {number}. </span>}
          {asset.caption ?? ''}
        </figcaption>
      )}

      {callouts.length > 0 && (
        <ol className={s.callouts} id={calloutsId} data-testid={`kb-callouts-${asset.asset_key}`}>
          {callouts.map((c) => (
            <li key={`${c.n}-${c.label}`} className={s.callout}>
              <span className={s.calloutMark} aria-hidden="true">{c.n}</span>
              <span>
                {/* The screen reader gets the number in words rather than a
                    bare glyph, so the list reads as an annotation and not as a
                    run of digits. */}
                <span className={s.srOnly}>{`Callout ${c.n}: `}</span>
                {c.label}
              </span>
            </li>
          ))}
        </ol>
      )}

      {zoomed && load.kind === 'ready' && (
        <Modal
          {...zoomModalProps(asset, frameRef)}
          onClose={() => setZoomed(false)}
          className={s.zoomPanel}
        >
          <div className={s.zoomBar}>
            <p className={s.docMeta}>
              {[asset.caption, formatLabel(asset.content_type),
                formatBytes(asset.size_bytes),
                asset.revision ? `Revision ${asset.revision}` : null]
                .filter(Boolean).join(' · ')}
            </p>
            <Button onClick={() => setZoomed(false)}>Close</Button>
          </div>
          <img className={s.zoomImage} src={load.url} alt={alt} referrerPolicy="no-referrer" />
          {callouts.length > 0 && (
            <ol className={s.callouts}>
              {callouts.map((c) => (
                <li key={`z-${c.n}-${c.label}`} className={s.callout}>
                  <span className={s.calloutMark} aria-hidden="true">{c.n}</span>
                  <span><span className={s.srOnly}>{`Callout ${c.n}: `}</span>{c.label}</span>
                </li>
              ))}
            </ol>
          )}
        </Modal>
      )}
    </figure>
  )
}

/**
 * The figure a body REFERENCES and the manifest does not contain.
 *
 * The marker text is deliberately absent. An article whose figure was
 * withdrawn, superseded or simply is not visible to this reader must not start
 * printing `{{fig:…}}` at them — and the article itself stays readable, which
 * is the acceptance criterion this exists for.
 */
export function KbMissingFigure({ id }: { id: string }) {
  return (
    <div className={s.state} role="note" data-testid={`kb-figure-missing-${id}`}>
      <p className={s.stateText}>
        A figure referenced here isn’t available to you. The rest of this article is
        unaffected — if you need it, raise a support case and we’ll send it.
      </p>
    </div>
  )
}

function FigureState({ state, onRetry, asset }: {
  /* Narrowed to the states this component actually draws. `ready` is the
     caller's branch, and typing it out of the prop is what makes the
     exhaustive check below hold rather than merely look like it does. */
  state: Exclude<Load, { kind: 'ready' }>
  onRetry: () => void
  asset: KbAsset
}) {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <div
        className={s.skeleton}
        style={aspect(asset)}
        role="status"
        aria-busy="true"
        aria-label="Loading this figure"
        data-testid={`kb-figure-loading-${asset.asset_key}`}
      />
    )
  }
  return (
    <div
      className={s.state}
      role="status"
      data-testid={`kb-figure-failed-${asset.asset_key}`}
    >
      <p className={s.stateText}>
        {state.offline
          ? 'This figure couldn’t be loaded — the file store could not be reached.'
          : 'This figure couldn’t be loaded just now.'}
      </p>
      <Button onClick={onRetry} data-testid={`kb-figure-retry-${asset.asset_key}`}>
        Try again
      </Button>
    </div>
  )
}

/**
 * The accessibility contract this surface owes `@solum/ui`'s `Modal`.
 *
 * Extracted and SPREAD rather than typed inline, because the two properties
 * below are exactly the ones the Modal's own proofs depend on and neither is
 * observable from a server-rendered string — the overlay only exists after a
 * click. A mutation that dropped `restoreFocusTo` from the JSX came back GREEN
 * while this was inline, which is the finding that produced this function:
 * making the props a value makes them assertable.
 *
 *   label           REQUIRED and non-empty. `Modal` throws on a blank one at
 *                   runtime, and a dialog with no accessible name is announced
 *                   as the single word "dialog". Falls through caption → alt →
 *                   the figure id, so it can never be empty.
 *   restoreFocusTo  where focus goes on close. Without it `Modal` falls back to
 *                   whatever was focused at MOUNT, which for a figure opened
 *                   from a grid row can be an element that has since unmounted
 *                   — #1466 F-30B, where the fallback landed focus on <body>.
 *                   The frame outlives the overlay, so it is a valid target.
 */
export function zoomModalProps(
  asset: KbAsset,
  frameRef: RefObject<HTMLButtonElement | null>,
): { label: string; restoreFocusTo: RefObject<HTMLButtonElement | null>; testid: string } {
  const name = [asset.caption, asset.alt_text].find((v) => typeof v === 'string' && v.trim() !== '')
  return {
    label: (name ?? `Figure ${asset.asset_key}`).trim(),
    restoreFocusTo: frameRef,
    testid: `kb-figure-zoomed-${asset.asset_key}`,
  }
}

/**
 * Reserve the image's shape before its bytes arrive.
 *
 * Only when the publisher recorded both dimensions. Guessing 16/9 for a tall
 * screenshot would replace one layout shift with a wrong one.
 */
function aspect(asset: KbAsset) {
  return asset.width && asset.height
    ? { aspectRatio: `${asset.width} / ${asset.height}` }
    : undefined
}

/**
 * The callout manifest, made safe to render.
 *
 * `callouts` is publisher-supplied JSON, so nothing about its shape is
 * guaranteed. Each entry is reduced to a number and a STRING label; anything
 * that is not an object with usable text is dropped rather than rendered as
 * `[object Object]`. Nothing here becomes markup — the label is a React text
 * child, and there is no path from this data to an attribute or a URL.
 */
export function normaliseCallouts(raw: unknown): { n: number; label: string }[] {
  if (!Array.isArray(raw)) return []
  const out: { n: number; label: string }[] = []
  raw.forEach((entry, i) => {
    if (typeof entry === 'string') {
      const label = entry.trim()
      if (label) out.push({ n: out.length + 1, label })
      return
    }
    if (!entry || typeof entry !== 'object') return
    const c = entry as KbCallout & Record<string, unknown>
    const text = [c.label, c.text].find((v) => typeof v === 'string' && v.trim() !== '')
    if (typeof text !== 'string') return
    const n = typeof c.n === 'number' && Number.isFinite(c.n) && c.n > 0
      ? Math.floor(c.n)
      : i + 1
    out.push({ n, label: text.trim() })
  })
  return out.sort((a, b) => a.n - b.n)
}
